import {FC, ReactNode, useEffect, useState} from 'react';
import Pagination from './Pagination';
import {ViewMode} from '../enums/ViewMode';
import GridCollection from './GridCollection';
import ListCollection from './ListCollection';
import useMaintainScrollPosition from '../hooks/useMaintainScrollPosition';
import useLocalStorage from '../hooks/useLocalStorage';
import StringUtils from '../utils/StringUtils';

type TProps<T> = {
    id: string;
    items: T[];
    renderItems: Partial<Record<ViewMode, (item: T, index: number) => ReactNode>>;
    itemsPerPage: number;
    itemsPerRow?: number;
    itemsPerRowTablet?: number;
    itemsPerRowMobile?: number;
    viewMode?: ViewMode;
    columnGapClass?: string;
    rowGapClass?: string;
}

const PaginatedCollection: FC<TProps<any>> = ({id, items, renderItems, itemsPerPage, itemsPerRow = 1, itemsPerRowTablet, itemsPerRowMobile, viewMode = ViewMode.GridView, columnGapClass, rowGapClass}) => {
    const prepareScrollContext = useMaintainScrollPosition();
    // const [currentPage, setCurrentPage] = useLocalStorage<number>(`pagination/${id}`, 1);
    const [currentPage, setCurrentPage] = useState<number>(1);

    useEffect(() => {
        const totalPages = Math.ceil(items.length / itemsPerPage);
        if (currentPage > totalPages) {
            setCurrentPage(1); // Reset if currentPage is now out of range
        }
    }, [items, currentPage, itemsPerPage]);

    let paginatedItems = items;
    if (itemsPerPage !== Infinity) {
        const startIndex = (currentPage - 1) * itemsPerPage;
        paginatedItems = items.slice(startIndex, startIndex + itemsPerPage);
    }

    const handlePageChange = (element: HTMLElement, page: number) => {
        prepareScrollContext(element);
        setCurrentPage(page);
    }

    const viewModeComponents = {
        [ViewMode.GridView]: GridCollection,
        [ViewMode.ListView]: ListCollection,
        [ViewMode.ListLeftToRightView]: ListCollection,
    };

    const Container = viewModeComponents[viewMode];
    const className = `cr-${StringUtils.toClassName(id)}`;

    return (
        <div className={className}>
            {items && (items.length > 0) &&
                <>
                    <Container $itemsPerRow={itemsPerRow} $itemsPerRowTablet={itemsPerRowTablet} $itemsPerRowMobile={itemsPerRowMobile} rowGapClass={rowGapClass} columnGapClass={columnGapClass}>
                        {paginatedItems.map((item, index) => renderItems[viewMode]?.(item, index) ?? null)}
                    </Container>
                    <Pagination
                        totalItems={items.length}
                        itemsPerPage={itemsPerPage}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                    />
                </>
            }
        </div>
    );
}

export default PaginatedCollection;
